import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';
import { normalizeOrg } from '../../lib/utils';

interface SearchableSelectProps {
  options: { id: string; name: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({ 
  options, 
  value, 
  onChange, 
  placeholder,
  label 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(() => options.find(o => o.id === value), [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const normalizedSearch = searchTerm.toLowerCase().trim();
    return options.filter(o => 
      o.name.toLowerCase().includes(normalizedSearch) ||
      normalizeOrg(o.name).includes(normalizeOrg(normalizedSearch))
    );
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full min-w-[240px]" ref={containerRef}>
      {label && <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">{label}</label>}
      
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2 bg-white border rounded text-[10px] font-bold uppercase transition-all shadow-sm
          ${isOpen ? 'border-slate-900 ring-2 ring-slate-100' : 'border-slate-200 hover:border-slate-300'}`}
      >
        <span className={`truncate mr-2 ${selectedOption ? 'text-slate-900 font-black' : 'text-slate-400'}`}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <ChevronDown size={12} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-slate-900 rounded shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
              <input
                autoFocus
                type="text"
                placeholder="Type to filter..."
                className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded font-bold text-[10px] outline-none focus:border-slate-900 uppercase"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-300 hover:text-slate-900"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto no-scrollbar">
            <button
              onClick={() => { onChange(''); setIsOpen(false); setSearchTerm(''); }}
              className="w-full text-left px-4 py-3 text-[10px] font-black text-rose-500 uppercase hover:bg-rose-50 border-b border-slate-50 tracking-widest"
            >
              Clear Selection
            </button>
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center text-[10px] font-bold text-slate-300 uppercase italic">
                No matching options
              </div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => { onChange(option.id); setIsOpen(false); setSearchTerm(''); }}
                  className={`w-full flex items-center justify-between px-4 py-3 text-[10px] font-bold uppercase transition-colors text-left
                    ${value === option.id ? 'bg-slate-900 text-white font-black' : 'hover:bg-slate-50 text-slate-600'}`}
                >
                  <span className="truncate">{option.name}</span>
                  {value === option.id && <Check size={12} className="shrink-0 ml-2" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};